<template>
    <md-content class="md-scrollbar tasks">
        <div class="dashboard md-layout md-gutter md-alignment-horizontal">
            <TaskCell 
                :combinedCells="combinedCells" 
                :orgId="prom.orgId" 
                @delete-asset-doc="taskID => deleteAssetDoc(taskID)"
                @start-export="docID => startExport(docID)"
                />
        </div>
        <md-snackbar
            :md-position="'center'"
            :md-active.sync="snackbarActive"
            :md-duration="10000"
            md-persistent
            >
            <span>{{ snackbarMessage }}</span>
        </md-snackbar>
    </md-content>
</template>


<script>
/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */
import {
    organisationsCollection,
} from "@/firebase.js";
import { AuthService } from "@/services/auth.service";
import { AssetsService } from "@/services/assets.service";
import TaskCell from "@/components/TaskCell.vue"

export default {
    name: "Tasks",
    props: {
        blocks: {
            type: Array,
            required: false,
        },
    },
    components: {
        TaskCell
    },
    data: () => ({
        cells: [],
        ids: [],
        runs: {},
        prom: {},
        statuscheckinprogress: false,
        unsubscribeOperations: {},
        unsubscribeUploadOperations: {},
        unsubscribeOperationRequests: {},
        operations:[],
        uploadOperations: [],
        operationRequests:[],
        snackbarActive: false,
        snackbarMessage: "",
    }),
    created() {
        let self = this;

        this.subscription = AuthService.loggedUser$.subscribe((user) => {
            this.prom = user;
        });
        //create connection to firebase (above) and query for relevant run documents (below)

        this.unsubscribeOperations = organisationsCollection
            .doc(this.prom.orgId)
            .collection("operations")
            .where("submittedBy.uid", "==", this.prom.uid)
            .orderBy("creation_timestamp_ms", "desc")
            .onSnapshot((docs) => {
                AssetsService.updateAssets();
                self.clearCells();
                self.operations = []
                docs.forEach(function (doc) {
                    self.createCell(doc.data());
                });
            });
        // because user ID is files differenty for upload vs download, we need a second query
        this.unsubscribeUploadOperations = organisationsCollection
            .doc(this.prom.orgId)
            .collection("operations")
            .where("submittedBy", "==", this.prom.uid)
            .orderBy("createdAt", "desc")
            .onSnapshot(function (docs) {
                AssetsService.updateAssets();
                self.clearCells();
                self.uploadOperations = []
                docs.forEach(function (doc) {
                    self.createUploadCell(doc.data());
                });
            });

        this.unsubscribeOperationRequests = organisationsCollection
            .doc(this.prom.orgId)
            .collection("operationRequests")
            .where("submittedBy.uid", "==", this.prom.uid)
            .where("state", "==", "UNSUBMITTED")
            .orderBy("createdAt", "desc")
            .onSnapshot((docs) => {
                self.clearCells();
                self.operationRequests = []
                docs.forEach((doc) => {
                    let docData = doc.data()
                    let docID = doc.id;
                    self.createOperationRequestCell(docData, docID)
                });
            });
    },
    beforeDestroy() {
        //disconnect from firebase
        this.subscription.unsubscribe();
        this.unsubscribeOperations();
        this.unsubscribeOperationRequests();
        this.unsubscribeUploadOperations();
    },

    methods: {
        startExport: async function (operationDocID) {
            this.statuscheckinprogress = true;
            organisationsCollection
                .doc(this.prom.orgId)
                .collection("operationRequests")
                .doc(operationDocID)
                .update({
                    state: "SUBMITTED"
                }).then(() => {
                    this.statuscheckinprogress = false;
                })
        },
        createCell: function (task) {
            let cellID = this.cells.length;

            let createDate = new Date(task.creation_timestamp_ms);

            let cell = {
                //add entry to the cells list to display
                request: false,
                jobID: task.id,
                id: cellID,
                downloadlinks: [],
                state: task.state,
                startedAt: createDate,
                thumbnail: task.thumbnail,
                laststatuscheck: task.laststatuscheck,
                rundoc: task.rundocId,
                downloadFilename: task.downloadFilename,
                layerLabel: task.layerLabel,
                downloadPrefix: task.downloadPrefix,
                checkInProgress: false,
                cellType: 'download',
            };

            this.cells.push(cell);
            this.operations.push(cell);
        },
        createUploadCell: function (task) {
            if (task.task_type === "INGEST_TABLE") {
                let cellID = this.cells.length;

                // let createDate = new Date(task.creation_timestamp_ms);

                let cell = {
                    //add entry to the cells list to display
                    request: false,
                    jobID: task.uploadJob,
                    taskID: task.id,
                    id: cellID,
                    state: task.state,
                    error: task.error,
                    warnings: task.warnings,
                    startedAt: task.createdAt.toDate(),
                    laststatuscheck: task.updatedAt,
                    downloadFilename: task.filename,
                    checkInProgress: false,
                    cellType: 'upload',
                    asset: {
                        id: task.assetID,
                        name: task.filename,
                        type: "TABLE"
                    }
                };

                this.cells.push(cell);
                this.uploadOperations.push(cell);
            }
        },
        createOperationRequestCell: function (operationRequest, operationRequestID) {
            let createdDate = operationRequest.createdAt.toDate();

            let cellID = this.operationRequests.length;

            let cell = {
                // job id
                request: true,
                id: cellID,
                downloadlinks: [],
                state: operationRequest.state,
                error: operationRequest.error,
                warnings: operationRequest.warnings,
                startedAt: createdDate,
                thumbnail: operationRequest.thumbnail,
                // last status check
                rundoc: operationRequest.rundocId,
                downloadFilename: operationRequest.downloadFilename,
                layerLabel: operationRequest.layerLabel,
                downloadPrefix: operationRequest.downloadPrefix,
                checkInProgress: false,
                docID: operationRequestID
            }

            this.operationRequests.push(cell);
        },
        clearCells() {
            this.cells = [];
            this.ids = [];
            // cells cleared when new docs are added
            this.$emit("new-docs");
        },
        removeLayer(layer) {
            //removing tab entry on user request
            let included = [];
            for (let i = 0; i < this.cells.length; i++) {
                included.push(this.cells[i].jobID);
            }
            let index = included.indexOf(layer["taskstatus"]["id"]);
            if (index > -1) {
                this.cells.splice(index, 1);
            }
        },
        deleteAssetDoc(taskID){
            let docID = `${this.prom.uid}_${taskID}`;
            console.log('deleting doc', docID)

            let taskDoc = organisationsCollection
                .doc(this.prom.orgId)
                .collection("operations")
                .doc(docID)

            taskDoc.delete().then(() => {
                console.log('deleted')
                this.snackbarMessage = "Task record has been deleted, your asset will still be available in the assets tab."
                this.snackbarActive = true;
            }).catch((error) => {
                console.log('error deleting', error)
                this.snackbarMessage = "There was an error deleting the task record, please try again."
                this.snackbarActive = true;
            })
        }
    },
    computed: {
        combinedCells: function() {
            let cells = this.operationRequests.concat(this.operations, this.uploadOperations);
            cells.sort((a, b) => {
                return b.startedAt - a.startedAt;
            });
            cells.forEach((cell, index) => {
                cell.id = index;
            })
            return cells;
        }
    },
    watch: {
        operationRequests: {
            immediate: true,
            handler(newOperationRequests) {
                this.$emit('new-requests', newOperationRequests.length)
            }
            
        }
    }
};
</script>
